import { cx } from "@linaria/core";
import type { ImgHTMLAttributes, RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import { useActivityIndicator } from "../hooks/activity-indicator";

import * as styles from "./postloaded-image.style";
import { generateSrcSet } from "../utils/image.generate-src-set";
import { isPlatformSafari } from "../utils/platform.is-safari";
import { assertRefObject } from "../utils/assert-ref-object";

export { postloadedImageSelectors } from "./postloaded-image.style";

type Props = ImgHTMLAttributes<HTMLImageElement> & {
  ref?: RefObject<HTMLImageElement | null>;

  showImmediately?: boolean;
  alt: string;
  onReady?(): void;

  maxWidth?: number;
};

/**
 * renders an image that will fade in once fully loaded. In the case loading is
 * slow, then a placeholder will be displayed while loading continues.
 *
 * furthermore, if the image is (almost) immediately available, then it will be
 * displayed without fading is it is unnecessary in this case.
 */
export const PostloadedImage = function PostloadedImage({
  className,
  showImmediately,
  maxWidth,
  ref: externalRef,
  ...props
}: Props): React.JSX.Element {
  const internalRef = useRef<HTMLImageElement>(null);
  const ref = externalRef ?? internalRef;
  assertRefObject(ref);

  const [isLoaded, setLoaded] = useState(showImmediately ?? false);

  // In case the image loads after a very short delay then simply show it
  // immediately. Else, ensure that the placeholder is at least shown for a
  // little while before showing the image to avoid too many jumps at once.
  const placeholder = useActivityIndicator(!isLoaded, {
    delayMs: 75,
    durationMs: 150,
  });

  // Keep track of whether or not the placeholder actually was displayed
  const hasShownPlaceholderRef = useRef(false);

  useEffect(() => {
    if (!isLoaded && !placeholder.isActive()) {
      hasShownPlaceholderRef.current = false;
    } else if (placeholder.isActive()) {
      hasShownPlaceholderRef.current = true;
    }
  }, [placeholder, isLoaded]);

  // Ensure we properly update our initial state as soon as the reference to
  // the image is available.
  useEffect(() => {
    const image = ref.current;

    if (!image) {
      return;
    }

    setLoaded(image.complete);
    if (image.complete) {
      props.onReady?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  return (
    <span
      className={cx(
        styles.wrapper,
        className,
        placeholder.isActive() && "withPlaceholder",
        isLoaded && "loaded",
      )}
    >
      <img
        {...props}
        className={cx(
          styles.image,
          hasShownPlaceholderRef.current && "withTransition",
          isLoaded && "loaded",
        )}
        srcSet={
          props.src && props.sizes
            ? generateSrcSet(props.src as string, maxWidth)
            : undefined
        }
        ref={ref}
        alt={props.alt}
        loading={!isPlatformSafari() ? props.loading ?? "lazy" : props.loading}
        onLoad={() => {
          setLoaded(true);
          props.onReady?.();
        }}
      />
    </span>
  );
};
